import Api from "../../features/one-query/services/ApiServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ConversationData,
  conversationsActions,
} from "../one-query/conversationSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleNewConversationRequest,
  setLimitAlert,
  setNewConversation,
  setShowPlansModal,
  setSnackBarAlert,
  toggleFullWidthMode,
} from "../one-query/chatsSlice";
import {  checkInternet, ConversationName, StopModelsRequestParams } from "../../Helper";
import { UserState } from "../account/userSlice";
import { HandleErrorForLimitations } from "../../HandleErrorForLimitationsHelper";
interface ConversationCreation {
  FirstConversationPrompt: string;
  ConversationModels: {
    name: string;
    order: number;
    id: string;
  }[];
}
/*
create ConversationRequest Endpoint
*/

// function to create a ConversationRequest
const createConversationRequest = async ({
  conversationRequest,
  conversationWithVersions,
  handleSendRequest,
  query,
  attachmentFile,
  imageUpload
}: {
  conversationRequest: ConversationCreation | undefined;
  conversationWithVersions: any;
  handleSendRequest: any;
  query: string;
  attachmentFile: any;
  imageUpload:File[]|null
}) => {
  try {
    const response = await Api.post(
      "Conversation/CreateConversationWithModels",
      JSON.stringify(conversationRequest)
    );

    return response.data;
  } catch (e) {
    console.error("Error creating conversation:", e);
    throw e;
  }
};

// custom hook to create a ConversationRequest using useMutation
const useCreateConversationRequest = () => {
  return useMutation({
    mutationKey: ["createConversationRequest"],
    mutationFn: createConversationRequest,
  });
};

// Hook to create a conversation  by return fun make this query and dispatch data
const useCreateConversation = () => {
  const createConversationMutation = useCreateConversationRequest();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const createConversation = (
conversation: ConversationCreation | undefined, conversationWithVersions: any, handleSendRequest: any, query: string, attachmentFile: FileList | null, imageUpload: File[] | null
  ): Promise<{
    activeConId: string | undefined;
    conversationModelsIds: any[];
  }> => {
    // specify that this function returns a Promise
    return new Promise((resolve, reject) => {
      // Renaming 'conversation' to 'conversationRequest'
      createConversationMutation.mutate(
        {
          conversationRequest: conversation,
          conversationWithVersions,
          handleSendRequest,
          query,
          attachmentFile,
          imageUpload
        },
        {
          onSuccess: (data) => {
            let newConversation: ConversationData = data;
            if(newConversation.name?.length === 0){
              newConversation.name = ConversationName;
            }
            dispatch(conversationsActions.setIsNewConversation(false));
            dispatch(setNewConversation(false));
            dispatch(conversationsActions.newConversationSuccess(data));
            dispatch(conversationsActions.setActiveConversation(data?.id));
            dispatch(
              conversationsActions.getLastActiveModels(conversationWithVersions)
            );
            let conversationModelsIds = [];
            let activeConId = data?.id;
            let conversationConversationModels =
              data?.conversationConversationModels;

            for (let i = 0; i < conversationConversationModels?.length; i++) {
              conversationModelsIds.push(conversationConversationModels[i].id);
            }
              handleSendRequest(
                query,
                attachmentFile,
                imageUpload,
                conversationModelsIds,
                activeConId
              );
              dispatch( conversationsActions.setConversationModelsIDs(conversationModelsIds)
            );

            resolve({ activeConId, conversationModelsIds });
            navigate(`/conversation/${activeConId}`);
          },
          onError: (error: any) => {
            let createConversationError = error?.response?.data?.error;
            if (createConversationError.includes("limit")) {
              dispatch(setLimitAlert({show: true ,message: createConversationError ,btnTitle: 'Go Premium',
                btnAction:"OpenUpgradePlans"}),)
            }else {
              const checkInternetStable = checkInternet();
              checkInternetStable.then((res) => {
                dispatch(setSnackBarAlert({
                  show: true,
                  message: res?createConversationError:"Internet connection is unstable" ,
                  alertType: res?"error":"warning"
                }))
              });
             
            }
          },
        }
      );
    });
  };

  return createConversation;
};

// ========================================================================================================
/*
Get ConversationRequest Endpoint
*/
export interface getConversationTypes{
  StartDate?: any,
  EndDate?: any,
  Page?: number | null,
  ItemsPerPage?: number | null,
  SortBy?: string | null,
  PersonaIds?: any,
  SortDesc?: boolean | null,
  Search?: string | null,
}
// function to get a ConversationRequest
const getConversationRequest = async (params:getConversationTypes) => {
  try {
    const queryParams = new URLSearchParams();

// Only add the parameters if they are not null or undefined
if (params.StartDate) queryParams.append('StartDate', String(params.StartDate));
if (params.EndDate) queryParams.append('EndDate', String(params.EndDate));
if (params.Page) queryParams.append('Page', String(params.Page)); // Convert to string
if (params.ItemsPerPage) queryParams.append('ItemsPerPage', String(params.ItemsPerPage)); // Convert to string
if (params.SortBy) queryParams.append('SortBy', String(params.SortBy));
if (params.PersonaIds) queryParams.append('PersonaIds', String(params.PersonaIds)); // Convert to string
if (params.SortDesc) queryParams.append('SortDesc', String(params.SortDesc)); // Convert to string
if (params.Search) queryParams.append('Search', String(params.Search));

const response = await Api.get(`Conversation?${queryParams.toString()}`);

    return response.data; // Return the data
  } catch (e) {
    throw e;
  }
};


// custom hook to get a ConversationRequest using useQuery
const useGetConversationHistory = (params:getConversationTypes) => {
  return useQuery({
    queryKey: ["getConversationRequest",params],
    queryFn:() => getConversationRequest(params),
    enabled:!!params
})
};

// ========================================================================================================

/*
Delete ConversationRequest Endpoint
*/

// function to delete a ConversationRequest
const deleteConversationRequest = async (id: string) => {
  try {
    const response = await Api.delete(`Conversation/${id}`);
    return response.data;
  } catch (e) {}
};

// custom hook to delete a ConversationRequest using useMutation
const useDeleteConversationRequest = () => {
  return useMutation({
    mutationKey: ["deleteConversationRequest"],
    mutationFn: deleteConversationRequest,
  });
};

// hook to delete a conversation by return fun make this query and dispatch data
const useDeleteConversation = () => {
  const queryClient = useQueryClient();
  const deleteConversationMutation = useDeleteConversationRequest();
  const dispatch = useDispatch();
  const deleteConversation = (id: string) => {
    deleteConversationMutation.mutate(id, {
      onSuccess: (data) => {
        dispatch(conversationsActions.deleteConversationSuccess(id));
        queryClient.invalidateQueries({ queryKey: ["getConversationRequest"] });
      },
      onError: (error) => {
        dispatch(conversationsActions.deleteConversationError(error.message));
      },
    });
  };
  return deleteConversation;
};
// ========================================================================================================

/*
Update ConversationRequest Endpoint
*/

// function to update a ConversationRequest
const updateConversationRequest = async (
  ConversationRequest: ConversationData
) => {
  try {
    const response = await Api.patch(
      `Conversation/${ConversationRequest.id}`,
      JSON.stringify(ConversationRequest)
    );
    return response.data;
  } catch (e) {}
};

// custom hook to update a ConversationRequest using useMutation
const useUpdateConversationRequest = () => {
  return useMutation({
    mutationKey: ["updateConversationRequest"],
    mutationFn: updateConversationRequest,
  });
};

// hook to update a conversation by return fun make this query and dispatch data
const useUpdateConversation = () => {
  const queryClient = useQueryClient();
  const updateConversationMutation = useUpdateConversationRequest();
  const dispatch = useDispatch();
  const updateConversation = (conversation: ConversationData) => {
    updateConversationMutation.mutate(conversation, {
      onSuccess: (data) => {
        dispatch(conversationsActions.updateConversationSuccess(data));
        queryClient.invalidateQueries({ queryKey: ["getConversationRequest"] });
      },
      onError: (error) => {
        dispatch(conversationsActions.updateConversationError(error.message));
      },
    });
  };
  return updateConversation;
};
// ========================================================================================================

//Update Persona in anonnymonus

const updateConversationAnnonymonusRequest = async (
  ConversationRequest: ConversationData
) => {
  try {
    const response = await Api.patch(
      `AnonymousConversation/Patch?id=${ConversationRequest.id}`,
      JSON.stringify(ConversationRequest)
    );
    return response.data;
  } catch (e) {}
};

// custom hook to update a ConversationRequest using useMutation
const useUpdateConversationAnnonymonusRequest = () => {
  return useMutation({
    mutationKey: ["updateConversationAnnonymonusRequest"],
    mutationFn: updateConversationAnnonymonusRequest,
  });
};

// hook to update a conversation by return fun make this query and dispatch data
const useUpdateConversationAnnonyPersona = () => {
  const queryClient = useQueryClient();
  const updateConversationMutation = useUpdateConversationAnnonymonusRequest();
  const dispatch = useDispatch();
  const updateConversation = (conversation: ConversationData) => {
    updateConversationMutation.mutate(conversation, {
      onSuccess: (data) => {
        dispatch(conversationsActions.updateConversationSuccess(data));
        queryClient.invalidateQueries({ queryKey: ["getConversationRequest"] });
      },
      onError: (error) => {
        dispatch(conversationsActions.updateConversationError(error.message));
      },
    });
  };
  return updateConversation;
};

/*
Clear All ConversationRequest Endpoint
*/

// function to clear all ConversationRequests
const clearAllConversationRequests = async () => {
  try {
    const response = await Api.post(`Conversation/ClearAllConversation`);
    return response.data;
  } catch (e) {
    throw e;
  }
};

// custom hook to clear all ConversationRequests using useMutation
const useClearAllConversationRequests = () => {
  return useMutation<Error>({
    mutationKey: ["clearAllConversationRequests"],
    mutationFn: clearAllConversationRequests,
  });
};

// hook to clear all conversations by return fun make this query and dispatch data
const useClearAllConversations = () => {
  const clearAllConversationMutation = useClearAllConversationRequests();
  const dispatch = useDispatch();
  const clearAllConversations = () => {
    clearAllConversationMutation.mutate(undefined, {
      onSuccess: () => {
        dispatch(conversationsActions.clearAllConversations());
        dispatch(toggleFullWidthMode(""));
      },
      onError: (error) => {
        dispatch(conversationsActions.deleteConversationError(error.message));
      },
    });
  };
  return clearAllConversations;
};

// ======================================================================================
interface EditMessageParams {
  messageId: string,
  newPrompt: string,
  connectionId: string,
  dispatch:any,
  user: UserState,
  isGenerateImage:boolean,
  isNeedWebSearch: boolean,
  outputLanguage: string,
}

// Edit message request function
const editMessageRequest = async (data: EditMessageParams) => {
  const { user, dispatch } = data;
  const requestData = {
    messageId: data.messageId,
    connectionId: data.connectionId,
    isGenerateImage: data.isGenerateImage,
    isNeedWebSearch: data.isNeedWebSearch,
    outputLanguage: data.outputLanguage,
    newPrompt: data.newPrompt
  }
  try {
    const response = await Api.post("Chat/EditMessage",requestData);
    return response.data;
  } catch (e: any) {
    HandleErrorForLimitations({user,dispatch,errorMessage:e?.response?.data})
    throw e;
  }
};

// Hook to query the edit message
const useEditMessageRequest = () => {
  return useMutation({
    mutationKey: ["editMessageRequest"],
    mutationFn: (data: EditMessageParams) => editMessageRequest(data),
  });
};


// Function to get last active models
const getLastActiveModels = async (id: string) => {
  try {
    const response = await Api.post(`Conversation/${id}/GetActiveModels`);
    return response.data;
  } catch (e) {
    throw e;
  }
};

// Custom hook to handle the mutation
const useGetLastActiveModelsRequest = () => {
  return useMutation({
    mutationKey: ["getLastActiveModels"],
    mutationFn: getLastActiveModels,
  });
};

// Hook to clear all conversations and return a function to make the query
const useGetLastActiveModels = () => {
  const { mutate } = useGetLastActiveModelsRequest();
  const dispatch = useDispatch();
  const [loading, setIsLoading] = useState(false);
  const getLastActiveModels = (id: string) => {
    setIsLoading(true);
    mutate(id, {
      onSuccess: (res) => {
        setIsLoading(false);
        dispatch(conversationsActions.getLastActiveModelsFromBackend(res));
      },
      onError: (e) => {
        setIsLoading(false);
        const ifIdNotValid = e.message.includes(
          "Request failed with status code 400"
        );
        if (ifIdNotValid) {
          dispatch(
            setSnackBarAlert({
              show: true,
              message: "This conversation doesn't exist.",
              alertType: "warning",
            })
          );
        }
      },
    });
  };

  return { getLastActiveModels, loading };
};

export default useGetLastActiveModels;

// ======================================================================================
const stopModelsRequest = async ({
  connectionId,
  param,
}: StopModelsRequestParams) => {
  try {
    const response = await Api.post(
      `Chat/StopModels?connectionId=${connectionId}`,
      {
        ...param,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useStopModelsRequest = () => {
  return useMutation({
    mutationKey: ["stopModelsRequest"],
    mutationFn: stopModelsRequest,
  });
};

// Hook to stop all models
const useStopModels = () => {
  const stopModelsRequest = useStopModelsRequest();
  const stopModels = ({ connectionId, param }: StopModelsRequestParams) => {
    stopModelsRequest.mutate(
      {
        connectionId,
        param,
      },
      {
        onSuccess: (res) => {
          console.log(res);
        },
        onError: (error) => {
          console.log(error);
        },
      }
    );
  };

  return stopModels;
};

export {
  useCreateConversation,
  useGetConversationHistory,
  useDeleteConversation,
  useUpdateConversation,
  useUpdateConversationAnnonyPersona,
  useClearAllConversations,
  useEditMessageRequest,
  useStopModels,
};
